import { Link } from 'react-router-dom';

interface Props {
  variant?: 'dark' | 'gold';
  title: string;
  subtitle?: string;
  primaryLabel?: string;
  showPhone?: boolean;
  bgImage?: string;
}

export default function SectionMidCTA({
  variant = 'dark',
  title,
  subtitle,
  primaryLabel = 'Schedule Free Consultation',
  showPhone = true,
  bgImage,
}: Props) {
  const isDark = variant === 'dark';

  return (
    <section className="relative py-24 overflow-hidden">
      {/* Background */}
      {bgImage && (
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url('${bgImage}')` }} />
      )}

      {/* Color overlay */}
      {isDark ? (
        <div className="absolute inset-0" style={{ background: bgImage ? 'linear-gradient(135deg, rgba(5,10,18,0.97) 0%, rgba(10,20,40,0.92) 100%)' : 'linear-gradient(135deg, #050a12 0%, #0a1428 50%, #111e30 100%)' }} />
      ) : (
        <div className="absolute inset-0" style={{ background: bgImage ? 'linear-gradient(135deg, rgba(180,135,50,0.96) 0%, rgba(220,185,90,0.94) 100%)' : 'linear-gradient(135deg, #b8883a 0%, #c9a84c 40%, #e0c272 100%)' }} />
      )}

      {/* Subtle radial glow */}
      {isDark && (
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] bg-[#c9a84c]/8 rounded-full blur-3xl" />
      )}

      {/* Top micro-line */}
      {isDark && <div className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: 'linear-gradient(90deg, transparent, #c9a84c50, transparent)' }} />}
      {!isDark && <div className="absolute top-0 left-0 right-0 h-[2px] bg-white/30" />}

      {/* Pattern dots */}
      <div className="absolute inset-0 opacity-[0.035]" style={{ backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.8) 1px, transparent 0)', backgroundSize: '24px 24px' }} />

      <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
        {/* Label marker */}
        {isDark && (
          <div className="inline-flex items-center gap-2.5 mb-6">
            <div className="w-6 h-[2px] bg-[#c9a84c]/60" />
            <span className="text-[#c9a84c] text-[11px] font-bold uppercase tracking-[0.28em]">Act Now</span>
            <div className="w-6 h-[2px] bg-[#c9a84c]/60" />
          </div>
        )}

        <h2
          className={`font-bold leading-[1.1] mb-5 ${isDark ? 'text-white' : 'text-[#030710]'}`}
          style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(30px, 4vw, 52px)' }}
        >
          {title}
        </h2>

        {subtitle && (
          <p className={`text-xl leading-relaxed mb-10 max-w-xl mx-auto ${isDark ? 'text-white/60' : 'text-[#030710]/65'}`}>
            {subtitle}
          </p>
        )}

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link
            to="/contact"
            className={`group inline-flex items-center justify-center gap-2.5 px-9 py-4 font-bold text-[15px] rounded-xl transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] cursor-pointer whitespace-nowrap ${
              isDark
                ? 'bg-[#c9a84c] text-[#050a12] hover:bg-[#dbb85a]'
                : 'bg-[#050a12] text-white hover:bg-[#0a1428]'
            }`}
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-calendar-check-line text-base" />
            </div>
            {primaryLabel}
          </Link>
          {showPhone && (
            <a
              href="tel:9795300250"
              className={`inline-flex items-center justify-center gap-2.5 px-9 py-4 border-2 font-semibold text-[15px] rounded-xl transition-all duration-200 cursor-pointer whitespace-nowrap ${
                isDark
                  ? 'border-white/20 text-white hover:border-[#c9a84c] hover:text-[#c9a84c]'
                  : 'border-[#030710]/25 text-[#030710] hover:border-[#030710]/60'
              }`}
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-phone-fill text-base" />
              </div>
              Call (979) 530-0250
            </a>
          )}
        </div>
      </div>
    </section>
  );
}
